'use client'

import React, { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { SectionWrapper } from '@/components/SectionWrapper'
import { Media } from '@/components/Media'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { normalizeSectionSettings, getBackgroundFromShade } from '@/utilities/sectionSettings'
import { cn } from '@/utilities/ui'
import type { QuoteBlock as QuoteBlockType } from '@/payload-types'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import { renderSimpleLexicalContent } from '@/utilities/renderSimpleLexical'

// Separate component for background image mode to ensure hooks are only called when needed
const QuoteWithBackground: React.FC<
  Pick<
    QuoteBlockType,
    'height' | 'quoteText' | 'attribution' | 'textAlignment' | 'quoteStyle' | 'settings'
  > & {
    backgroundImage: NonNullable<QuoteBlockType['backgroundImage']>
  }
> = ({
  backgroundImage,
  height,
  quoteText,
  attribution,
  textAlignment = 'left',
  quoteStyle = 'statement',
  settings,
}) => {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })

  // Parallax effect for background images
  const y = useTransform(scrollYProgress, [0, 1], [-80, 80])

  // Height classes
  const heightClasses = {
    full: 'h-screen',
    large: 'h-[80vh]',
    medium: 'h-[60vh]',
  }

  // Alignment classes
  const alignmentClasses: Record<string, string> = {
    left: 'text-center lg:text-left',
    center: 'text-center',
    right: 'text-center lg:text-right',
  }

  // Quote style classes
  const quoteStyleClasses: Record<string, string> = {
    statement: 'subtitle-2xl',
    standard: 'subtitle-xl',
  }

  const attributionStyleClasses: Record<string, string> = {
    statement: 'subtitle-lg',
    standard: 'subtitle-md',
  }

  // Overlay styles (hardcoded: 50% black)
  const overlayStyles = {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  }

  return (
    <section
      ref={sectionRef}
      className={cn(
        'relative w-full overflow-hidden',
        heightClasses[height || 'full'],
        settings?.isDarkMode && 'data-theme-dark',
      )}
    >
      {/* Background Image with Parallax */}
      <motion.div className="absolute inset-0 will-change-transform" style={{ y }}>
        <Media
          resource={typeof backgroundImage === 'object' ? backgroundImage : null}
          alt=""
          fill
          priority
          imgClassName="object-cover"
          size="100vw"
        />
      </motion.div>

      {/* Overlay */}
      <div className="absolute inset-0" style={overlayStyles} />

      {/* Content Container */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative z-10 w-full max-w-5xl px-8">
          <MotionFadeIn>
            <div className={cn('flex flex-col gap-2', alignmentClasses[textAlignment || 'left'])}>
              {/* Quote Text - White/Light by default */}
              <div className="space-y-3">
                {renderSimpleLexicalContent(quoteText as DefaultTypedEditorState | null, {
                  paragraphClassName: `${quoteStyleClasses[quoteStyle || 'statement']} text-white`,
                })}
              </div>

              {/* Attribution - White/Light by default */}
              {attribution && (
                <p
                  className={`${attributionStyleClasses[quoteStyle || 'statement']} text-white/90 uppercase`}
                >
                  {attribution}
                </p>
              )}
            </div>
          </MotionFadeIn>
        </div>
      </div>
    </section>
  )
}

export const QuoteClient: React.FC<QuoteBlockType> = ({
  enableBackgroundImage,
  backgroundImage,
  height = 'full',
  quoteText,
  attribution,
  textAlignment = 'left',
  quoteStyle = 'statement',
  settings,
}) => {
  // Check if we should use background image mode
  const hasBackgroundImage =
    enableBackgroundImage && backgroundImage && typeof backgroundImage === 'object'

  // If background image mode is enabled and image exists, render full-width version
  if (hasBackgroundImage) {
    return (
      <QuoteWithBackground
        backgroundImage={backgroundImage}
        height={height || 'full'}
        quoteText={quoteText}
        attribution={attribution}
        textAlignment={textAlignment}
        quoteStyle={quoteStyle}
        settings={settings}
      />
    )
  }

  // Alignment classes
  const alignmentClasses: Record<string, string> = {
    left: 'text-center lg:text-left',
    center: 'text-center',
    right: 'text-center lg:text-right',
  }

  // Quote style classes
  const quoteStyleClasses: Record<string, string> = {
    statement: 'subtitle-2xl',
    standard: 'subtitle-xl',
  }

  const attributionStyleClasses: Record<string, string> = {
    statement: 'subtitle-lg',
    standard: 'subtitle-md',
  }

  // Default mode: Use SectionWrapper (existing behavior)
  const normalizedSettings = normalizeSectionSettings(settings)
  // Block forces a light-gray default when no backgroundShade is set.
  const background = getBackgroundFromShade(normalizedSettings?.backgroundShade || 'neutral-50')

  return (
    <SectionWrapper
      settings={normalizedSettings}
      background={background}
      className="relative overflow-hidden"
    >
      <div className="relative z-10">
        <div className="flex items-center min-h-[450px]">
          <div
            className={cn(
              'w-full max-w-5xl flex flex-col gap-4 px-3',
              textAlignment === 'left' && 'lg:ml-0',
              textAlignment === 'center' && 'lg:mx-auto',
              textAlignment === 'right' && 'lg:ml-auto',
            )}
          >
            {/* Quote Text */}
            <div className={alignmentClasses[textAlignment || 'left']}>
              <div className="space-y-3">
                {renderSimpleLexicalContent(quoteText as DefaultTypedEditorState | null, {
                  paragraphClassName: `${quoteStyleClasses[quoteStyle || 'statement']} text-primary`,
                })}
              </div>
            </div>

            {/* Attribution */}
            {attribution && (
              <div className={alignmentClasses[textAlignment || 'left']}>
                <p
                  className={`${attributionStyleClasses[quoteStyle || 'statement']} text-text-brand uppercase`}
                >
                  {attribution}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
  )
}
